import { showHttpError } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { createAsyncThunk } from '@reduxjs/toolkit'


import { GROUP_MAPPING_STATES, GROUP_PAGE_SIZE } from './constants'
import { transformGroupMappings } from './helpers'
import { newUserGroupsSelector } from './selectors'
import { listGroups } from '../../user_groups'

const { ACTIVATED: GROUP_MAPPING_ACTIVATED, SYNCING: GROUP_MAPPING_SYNCING } = GROUP_MAPPING_STATES


export const fetchUserGroupsAndGroupMappings = createAsyncThunk(
  'organizationSettings/groupsScim/fetchUserGroupsAndGroupMappings',
  async (_, { dispatch, rejectWithValue }) => {
    const [userGroupsSuccess, userGroupsResponse] = await listGroups({ filter_term: '', page: 0, page_size: GROUP_PAGE_SIZE })

    if (!userGroupsSuccess) {
      dispatch(showHttpError(userGroupsResponse))
      return rejectWithValue({})
    }

    const [groupMappingsSuccess, groupMappingsResponse] = await httpRequest({
      url: '/api/organization/group_mappings'
    })

    if (!groupMappingsSuccess) {
      dispatch(showHttpError(groupMappingsResponse))
      return rejectWithValue({})
    }

    return { userGroupsResponse, groupMappingsResponse }
  }
)

export const fetchAvailableAttributeValues = createAsyncThunk(
  'organizationSettings/groupsScim/fetchAvailableDepartments',
  async (_, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/group_mappings/available_attribute_values',
      method: 'GET',
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue({})
    }

    return response
  }
)


export const saveGroupScimConfiguration = createAsyncThunk(
  'organizationSettings/groupsScim/saveGroupScimConfiguration',
  async ({ defaultUserGroupId, applyToExisting, mappings }, { dispatch, getState, rejectWithValue }) => {
    const newUserGroups = newUserGroupsSelector(getState())

    const mappingsToSave = transformGroupMappings(mappings)

    const userGroups = newUserGroups.filter(
      ({ id }) => mappingsToSave.find((mapping) => mapping.user_group_id === id) || defaultUserGroupId === id
    ).map(
      ({ id, originalName }) => (
        {
          uuid: id,
          name: originalName
        }
      )
    )

    const data = {
      apply_to_existing: applyToExisting,
      scim_configuration: {
        default_user_group_id: defaultUserGroupId,
        group_mapping_state: GROUP_MAPPING_ACTIVATED
      },
      group_mappings: mappingsToSave,
      organization: {
        user_groups: userGroups
      }
    }

    const [success, response] = await httpRequest({
      url: '/api/organization/group_mappings/batch_update',
      method: 'PUT',
      data
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    const scimSettings = response ? { ...response } : {}
    if (scimSettings?.group_mapping_state && applyToExisting) {
      scimSettings.group_mapping_state = GROUP_MAPPING_SYNCING
    }

    dispatch(fetchUserGroupsAndGroupMappings())
    dispatch(fetchAvailableAttributeValues())
    return scimSettings
  }
)

export const fetchPreviewInformation = createAsyncThunk(
  'organizationSettings/groupsScim/fetchPreviewInformation',
  async (
    { mappings, applyToExisting, defaultUserGroupId },
    { dispatch, rejectWithValue }
  ) => {
    if (!applyToExisting) {
      return { mappings }
    }

    const mappingsToPreview = transformGroupMappings(mappings)

    const data = {
      group_mappings: mappingsToPreview,
      default_user_group_id: defaultUserGroupId
    }

    const [success, previewResponse] = await httpRequest({
      url: '/api/organization/group_mappings/preview',
      method: 'POST',
      data
    })

    if (!success) {
      dispatch(showHttpError(previewResponse))
      return rejectWithValue({})
    }

    return { mappings, previewResponse }
  }
)

export const fetchScimSettings = createAsyncThunk(
  'organizationSettings/groupsScim/fetchScimSettings',
  async (_, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/scim_configuration',
      method: 'GET',
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue({})
    }

    return response || {}
  })

export const destroyScimConfiguration = createAsyncThunk(
  'organizationSettings/groupsScim/destroyScimConfiguration',
  async (_, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/scim_configuration',
      method: 'DELETE'
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    return {}
  })

export const toggleConfiguration = createAsyncThunk(
  'organizationSettings/groupsScim/toggleConfiguration',
  async (callback, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/scim_configuration/toggle',
      method: 'POST',
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    callback?.()
    return response
  })

export const generateBearerToken = createAsyncThunk(
  'organizationSettings/groupsScim/generateBearerToken',
  async (tokenDuration, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/scim_bearer_tokens',
      method: 'POST',
      data: { token_duration: tokenDuration }
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    return response
  })

export const destroyBearerToken = createAsyncThunk(
  'organizationSettings/groupsScim/destroyBearerToken',
  async ({ token, callback }, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: `/api/organization/scim_bearer_tokens/${token.id}`,
      method: 'DELETE',
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    callback?.()
    return token
  })

export const toggleGroupMappingState = createAsyncThunk(
  'organizationSettings/groupsScim/toggleGroupMappingState',
  async (_, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/organization/scim_configuration/toggle_group_mapping',
      method: 'POST'
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response)
    }

    dispatch(fetchUserGroupsAndGroupMappings())
    return response || {}
  })
